import { Form, useFormikContext } from 'formik';
import { useMemo, useState } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { FormControl } from '@@/form-components/FormControl';
import { FormSection } from '@@/form-components/FormSection';
import { LoadingButton } from '@@/buttons';
import { Option } from '@@/form-components/PortainerSelect';

import { Chart } from '../types';
import { useHelmChartValues } from '../helmChartSourceQueries/useHelmChartValues';
import { HelmValuesInput } from '../components/HelmValuesInput';
import { RegistryValue } from '../components/HelmRegistrySelect';
import { ChartVersion } from '../helmChartSourceQueries/useHelmRepoVersions';
import { ManifestPreviewFormSection } from '../components/ManifestPreviewFormSection';
import { HelmVersionSelect } from '../components/HelmVersionSelect';

import { HelmInstallFormValues } from './types';

type Props = {
  selectedChart: Chart;
  namespace?: string;
  name?: string;
  versionOptions: Option<ChartVersion>[];
  isVersionsLoading: boolean;
  isRegistryAvailable: boolean;
  selectedRegistry: RegistryValue | null;
};

export function HelmInstallInnerForm({
  selectedChart,
  namespace,
  name,
  versionOptions,
  isVersionsLoading,
  isRegistryAvailable,
  selectedRegistry,
}: Props) {
  const environmentId = useEnvironmentId();
  const [previewIsValid, setPreviewIsValid] = useState(false);
  const { values, setFieldValue, isSubmitting } =
    useFormikContext<HelmInstallFormValues>();

  const selectedVersion: ChartVersion | null = useMemo(
    () =>
      versionOptions.find((v) => v?.value?.version === values.version)?.value ??
      versionOptions[0]?.value ??
      null,
    [versionOptions, values.version]
  );

  const repoParams = selectedRegistry?.isOCI
    ? {
        registryId: selectedRegistry.registryId,
        environmentId,
      }
    : {
        repo: selectedChart.repo,
      };
  // use isLatestVersionFetched to cache the latest version, to avoid duplicate fetches
  const isLatestVersionFetched =
    // if no version is selected, the latest version gets fetched
    !versionOptions.length ||
    // otherwise check if the selected version is the latest version
    (selectedVersion?.version === versionOptions[0]?.value?.version &&
      selectedVersion?.repo === versionOptions[0]?.value?.repo);
  const chartValuesRefQuery = useHelmChartValues(
    {
      chart: selectedChart.name,
      version: values?.version,
      ...repoParams,
    },
    isLatestVersionFetched
  );

  const payload = useMemo(
    () => ({
      name: name || '',
      namespace: namespace || '',
      chart: selectedChart.name,
      version: values?.version,
      repo: selectedRegistry?.registryId ? undefined : selectedChart.repo,
      registryId: selectedRegistry?.registryId,
      values: values.values,
    }),
    [
      name,
      namespace,
      selectedChart.name,
      values?.version,
      selectedChart.repo,
      values.values,
      selectedRegistry?.registryId,
    ]
  );

  return (
    <Form className="form-horizontal">
      <div className="form-group !m-0">
        <FormSection title="Install options" className="mt-4">
          <FormControl label="Version" inputId="version-input">
            <HelmVersionSelect
              value={selectedVersion}
              options={versionOptions}
              onChange={(version: ChartVersion | null) => {
                setFieldValue('version', version?.version);
              }}
              data-cy="helm-version-input"
              isLoading={isVersionsLoading}
            />
          </FormControl>
          <HelmValuesInput
            values={values.values}
            setValues={(values) => setFieldValue('values', values)}
            valuesRef={chartValuesRefQuery.data?.values ?? ''}
            isValuesRefLoading={chartValuesRefQuery.isInitialLoading}
          />
        </FormSection>
        <ManifestPreviewFormSection
          payload={payload}
          onChangePreviewValidation={setPreviewIsValid}
          title="Manifest preview"
          environmentId={environmentId}
        />
      </div>

      <LoadingButton
        className="!ml-0 mt-5"
        loadingText="Installing Helm chart"
        isLoading={isSubmitting}
        disabled={
          !namespace || !name || !isRegistryAvailable || !previewIsValid
        }
        data-cy="helm-install"
      >
        Install
      </LoadingButton>
    </Form>
  );
}
